import React from 'react';

import {
  FormHelperText,
  type SxProps,
  TextField,
  type Theme,
} from '@mui/material';
import {
  FieldType,
  getControlOptions,
  getListItems,
  getRadioOptions,
} from '../../parsing/parser';
import type { FormFieldDef } from '../../types';

import { CheckboxGrouping } from '../input/CheckboxGrouping';
import { GroupedPercentileList } from '../input/GroupedPercentileList';
import { ItemizedPercentileList } from '../input/ItemizedPercentileList';
import { RadioGroupingContainer } from '../input/RadioGrouping';
import { FieldControlText } from './FieldControlText';

import { SingleSelect } from '../../../console/risk-eng/forms/admin/fields/CowbellRiskEngSingleSelectField';
import { Currency } from '../input/Currency';
import { FieldList } from '../input/FieldList';
import { JobList } from '../input/JobList';
import { NaicsClassDescription } from '../input/NaicsClassDescription';
import { FieldConfidenceChip } from './FieldConfidenceChip';
import { FieldControlLabel } from './FieldControlLabel';
import { determineIfRequired } from './helpers/form-layout-utils';
import { useErrorMessageForField } from './hooks/useErrorMessageForField';
import { useGetShowCondition } from './hooks/useGetShowCondition';
import {
  type FieldControlRenderArgs,
  useFormRenderers,
} from '../../context/FormRenderersContext';

type FieldControlProps = {
  name: string;
  value: any;
  onChange: (value: any) => void;
  meta: FormFieldDef;
  error?: string;
  conditions?: FormFieldDef['conditions'];
  disabled?: boolean;
  elementLabelSxProps?: SxProps<Theme>;
  confidenceScore?: number;
  showConfidence?: boolean;
  formId: string;
  // RAG props
  enableRag?: boolean;
  submissionId?: string;
};

/**
 * A field's control: the engine's own, or, where a host installs a renderer (`FormRenderersContext`), the host's.
 * With no renderer this is the engine's control and nothing else, so every other form renders as before.
 */
export const FieldControl = (props: FieldControlProps) => {
  const renderers = useFormRenderers();
  if (renderers?.fieldControl) {
    return <HostFieldControl {...props} render={renderers.fieldControl} />;
  }
  return <EngineFieldControl {...props} />;
};

type HostFieldControlProps = FieldControlProps & {
  render: (args: FieldControlRenderArgs) => React.ReactNode;
};

/** A field the host draws: shown, required and in error exactly as the engine's own control would be. */
const HostFieldControl = ({ render, ...props }: HostFieldControlProps) => {
  const errorMessage = useErrorMessageForField(props.meta.id);
  const isShowing = useGetShowCondition(props.conditions);

  if (!isShowing) {
    return null;
  }

  // The engine's control with no text has no label: the host draws the question itself.
  const unlabelled = {
    ...props.meta,
    text: undefined,
  } as unknown as FormFieldDef;
  return (
    <>
      {render({
        meta: props.meta,
        value: props.value,
        onChange: props.onChange,
        error: errorMessage,
        isRequired: determineIfRequired(
          props.meta.type,
          props.meta.schema,
          props.meta.required
        ),
        disabled: Boolean(props.disabled),
        confidence: props.showConfidence ? (
          <FieldConfidenceChip
            fieldId={props.meta.id}
            formId={props.formId}
            confidenceScore={props.confidenceScore}
            submissionId={props.submissionId}
          />
        ) : undefined,
        control: (
          <EngineFieldControl
            {...props}
            meta={unlabelled}
            showConfidence={false}
          />
        ),
      })}
    </>
  );
};

const EngineFieldControl = (props: FieldControlProps) => {
  const errorMessage = useErrorMessageForField(props.meta.id);
  const isShowing = useGetShowCondition(props.conditions);

  const commonProps = {
    name: props.name,
    value: props.value,
    onChange: props.onChange,
    confidenceScore: props.confidenceScore,
  };

  // Common RAG props to pass to all FieldControlLabel instances
  const ragLabelProps = {
    enableRag: props.enableRag,
    submissionId: props.submissionId,
    fieldType: props.meta.type,
    fieldMeta: props.meta,
  };

  const hasLabel = props.meta.text != null;
  const isRequired = determineIfRequired(
    props.meta.type,
    props.meta.schema,
    props.meta.required
  );

  if (!isShowing) {
    return null;
  }

  if (
    props.meta.type == FieldType.Radio ||
    props.meta.type == FieldType.SingleSelect ||
    props.meta.type == FieldType.Checkbox
  ) {
    return (
      <RadioGroupingContainer
        {...commonProps}
        value={props.value}
        options={getRadioOptions(props.meta.valueOptions)}
        error={errorMessage}
        disabled={props.disabled}
        labelEl={
          <FieldControlLabel
            hasLabel={hasLabel}
            isRequired={isRequired}
            text={props.meta.text}
            sx={{ flex: 1, pr: 10 }}
            confidenceScore={props.confidenceScore}
            showConfidence={props.showConfidence}
            fieldId={props.meta.id}
            formId={props.formId}
            {...ragLabelProps}
          />
        }
      />
    );
  }

  if (
    props.meta.type == FieldType.CheckboxGrouping ||
    props.meta.type == FieldType.MultiSelect
  ) {
    return (
      <React.Fragment>
        <FieldControlLabel
          hasLabel={hasLabel}
          isRequired={isRequired}
          text={props.meta.text}
          confidenceScore={props.confidenceScore}
          showConfidence={props.showConfidence}
          fieldId={props.meta.id}
          formId={props.formId}
          {...ragLabelProps}
        />

        <CheckboxGrouping
          {...commonProps}
          options={getControlOptions(props.meta.valueOptions)}
          error={errorMessage}
          disabled={props.disabled}
        />
      </React.Fragment>
    );
  }

  if (props.meta.type == FieldType.Currency) {
    return (
      <React.Fragment>
        <FieldControlLabel
          hasLabel={hasLabel}
          isRequired={isRequired}
          text={props.meta.text}
          confidenceScore={props.confidenceScore}
          showConfidence={props.showConfidence}
          fieldId={props.meta.id}
          formId={props.formId}
          sx={{ textWrap: 'nowrap' }}
          {...ragLabelProps}
        />

        <Currency
          {...commonProps}
          onChange={(val) => commonProps.onChange({ value: val })}
          value={commonProps.value?.value}
          error={errorMessage}
          disabled={props.disabled}
        />
      </React.Fragment>
    );
  }

  if (props.meta.type == FieldType.Text) {
    return (
      <FieldControlText
        isRequired={isRequired}
        hasLabel={hasLabel}
        commonProps={commonProps}
        errorMessage={errorMessage}
        disabled={props.disabled}
        meta={props.meta}
        showConfidence={props.showConfidence}
        fieldId={props.meta.id}
        formId={props.formId}
        {...ragLabelProps}
      />
    );
  }

  if (props.meta.type == FieldType.ItemizedPercentileList) {
    return (
      <React.Fragment>
        <FieldControlLabel
          hasLabel={hasLabel}
          isRequired={isRequired}
          text={props.meta.text}
          confidenceScore={props.confidenceScore}
          showConfidence={props.showConfidence}
          fieldId={props.meta.id}
          formId={props.formId}
          {...ragLabelProps}
        />
        <ItemizedPercentileList
          {...commonProps}
          error={errorMessage}
          listItems={getListItems(props.meta.valueOptions)}
          value={commonProps.value}
          onChange={(nextVal) => props.onChange(nextVal)}
          disabled={props.disabled}
        />
      </React.Fragment>
    );
  }

  if (props.meta.type == FieldType.GroupedPercentileList) {
    return (
      <React.Fragment>
        <FieldControlLabel
          hasLabel={hasLabel}
          isRequired={isRequired}
          text={props.meta.text}
          confidenceScore={props.confidenceScore}
          showConfidence={props.showConfidence}
          fieldId={props.meta.id}
          formId={props.formId}
          {...ragLabelProps}
        />
        <GroupedPercentileList
          {...commonProps}
          schemaProps={props?.meta?.schema?.properties?.values?.items}
          onChange={(nextVal) => props.onChange(nextVal)}
          error={errorMessage}
          fieldId={props.meta.id}
          formId={props.formId}
        />
      </React.Fragment>
    );
  }

  if (props.meta.type == FieldType.JobList) {
    return (
      <React.Fragment>
        <FieldControlLabel
          hasLabel={hasLabel}
          isRequired={isRequired}
          text={props.meta.text}
          confidenceScore={props.confidenceScore}
          showConfidence={props.showConfidence}
          fieldId={props.meta.id}
          formId={props.formId}
          {...ragLabelProps}
        />

        <JobList
          name={props.name}
          error={errorMessage}
          disabled={props.disabled}
        />
      </React.Fragment>
    );
  }

  if (props.meta.type === FieldType.FieldList && props.meta.schema) {
    return (
      <>
        <FieldControlLabel
          hasLabel={hasLabel}
          isRequired={isRequired}
          text={props.meta.text || ''}
          confidenceScore={props.confidenceScore}
          showConfidence={props.showConfidence}
          fieldId={props.meta.id}
          formId={props.formId}
          {...ragLabelProps}
        />
        <FieldList
          name={props.name}
          addButtonLabel="Add"
          maxItems={props.meta.schema.properties?.values.maxItems}
          schemaProps={
            Array.isArray(props.meta.schema.properties?.values?.items) &&
            props?.meta?.schema?.properties?.values?.items[0]?.properties?.props
          }
          error={errorMessage}
          disabled={props.disabled}
          formId={props.formId}
        />
      </>
    );
  }

  if (props.meta.type == FieldType.NaicsClassDescription) {
    return (
      <NaicsClassDescription
        name={props.name}
        required={isRequired}
        error={errorMessage}
        disabled={props.disabled}
      />
    );
  }

  if (props.meta.type == FieldType.Dropdown) {
    return (
      <React.Fragment>
        <FieldControlLabel
          hasLabel={hasLabel}
          isRequired={isRequired}
          text={props.meta.text}
          confidenceScore={props.confidenceScore}
          showConfidence={props.showConfidence}
          fieldId={props.meta.id}
          formId={props.formId}
          {...ragLabelProps}
        />

        <SingleSelect
          {...commonProps}
          onChange={(e: any) => {
            commonProps.onChange({ value: e.target.value });
          }}
          value={commonProps.value?.value}
          options={props.meta.valueOptions}
          disabled={props.disabled}
        />
      </React.Fragment>
    );
  }

  if (props.meta.type === FieldType.Number) {
    return (
      <React.Fragment>
        <FieldControlLabel
          hasLabel={hasLabel}
          isRequired={isRequired}
          text={props.meta.text}
          confidenceScore={props.confidenceScore}
          showConfidence={props.showConfidence}
          fieldId={props.meta.id}
          formId={props.formId}
          {...ragLabelProps}
        />

        <TextField
          {...commonProps}
          onChange={(e) =>
            commonProps.onChange({ value: Number(e.target.value) })
          }
          value={commonProps.value?.value}
          error={Boolean(errorMessage)}
          type="number"
          disabled={props.disabled}
          sx={{
            '& .MuiInputBase-input': {
              height: '0.5rem',
            },
          }}
        />
        {errorMessage && (
          <FormHelperText sx={{ margin: 0 }}>{errorMessage}</FormHelperText>
        )}
      </React.Fragment>
    );
  }

  if (props.meta.type === FieldType.Date) {
    return (
      <React.Fragment>
        <FieldControlLabel
          hasLabel={hasLabel}
          isRequired={isRequired}
          text={props.meta?.text}
          confidenceScore={props.confidenceScore}
          showConfidence={props.showConfidence}
          fieldId={props.meta.id}
          formId={props.formId}
          {...ragLabelProps}
        />

        <TextField
          {...commonProps}
          onChange={(e) => commonProps.onChange({ value: e.target.value })}
          value={commonProps.value?.value}
          error={Boolean(errorMessage)}
          type="date"
          disabled={props.disabled}
          sx={{
            '& .MuiInputBase-input': {
              height: '0.5rem',
            },
          }}
        />
        {errorMessage && (
          <FormHelperText sx={{ margin: 0 }}>{errorMessage}</FormHelperText>
        )}
      </React.Fragment>
    );
  }

  return null;
};
